import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  getAssessmentQuestionsQuery,
  getStudentAssignmentQuestionSubmissionsQuery,
  getAssessmentToolsByQuestionSubmissionIdQuery,
  getQuestionTopicsQuery,
  getOrganizationGradesQuery,
  getOrganizationSubjectsQuery,
  getSchoolQuestionLibraryFeedQuery,
  getToddleQuestionLibraryFeedQuery,
  getSyllabusContentTopicsQuery,
  getWorksheetQuestionWiseInsightsQuery,
  getWorksheetScoreDistributionQueryV2,
  getWorksheetQuestionWiseInsightsV2Query,
  getWorksheetScoreDistributionQuery,
  getQuestionLibraryQuestionQuery,
  getStudentQuestionDetailsQuery,
  getStudentAssignmentQueryV2,
  getAssessmentQuestionsQueryV2,
  getStudentQuestionDetailsQueryV2,
  getAIConversationsQuery,
} from "./WorksheetQueries";
import {
  organizationResourceSectionFragment,
  questionFieldsFragments,
  questionFragments,
  questionSubmissionFragments,
  organizationResourceQuestionFragment,
  questionSubmissionConnectionFragments,
  organizationResourceQuestionFragmentV2,
  organizationResourceSectionFragmentV2,
} from "./WorksheetFragments";
import { studentAssignmentFragment } from "ClassRoom/modules/ClassRoomFragments";
import { getStudentAssignmentDetailsQueryV2 } from "ClassRoom/modules/ClassRoomQuery";

/**
 * @deprecated Use `getAssessmentQuestionsFromCacheV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const getAssessmentQuestionsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getAssessmentQuestionsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

/**
 * @deprecated Use `writeAssessmentQuestionsInCacheV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const writeAssessmentQuestionsInCache = (variables, data) => {
  try {
    client.writeQuery({
      query: getAssessmentQuestionsQuery,
      variables,
      data,
    });
  } catch (err) {
    return;
  }
};

export const getStudentAssignmentQuestionSubmissionsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getStudentAssignmentQuestionSubmissionsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getOrganizationResourceFromCache = (
  id,
  isSubQuestion,
  userType,
  showAssessmentTools = true
) => {
  const isStudent = _.isEqual(userType, "student");
  const fragment = isSubQuestion
    ? isStudent
      ? organizationResourceQuestionFragmentV2
      : organizationResourceQuestionFragment
    : isStudent
    ? organizationResourceSectionFragmentV2
    : organizationResourceSectionFragment;

  let data = {};
  if (isStudent) {
    data = {
      variables: {
        showAssessmentTools,
      },
    };
  }

  try {
    const queryData = client.readFragment({
      id: `OrganizationResource:${id}`,
      fragment,
      fragmentName: isSubQuestion
        ? "organizationResourceQuestionItem"
        : "organizationResourceSectionItem",
      ...data,
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeOrganizationResourceInCache = (id, data, isSubQuestion) => {
  try {
    client.writeFragment({
      id: `OrganizationResource:${id}`,
      fragment: isSubQuestion
        ? organizationResourceQuestionFragment
        : organizationResourceSectionFragment,
      fragmentName: isSubQuestion
        ? "organizationResourceQuestionItem"
        : "organizationResourceSectionItem",
      data,
    });
  } catch (e) {
    return;
  }
};

export const getQuestionFieldsFromCache = ({ id }) => {
  try {
    const queryData = client.readFragment({
      id: `QuestionFields:${id}`,
      fragment: questionFieldsFragments.questionFields,
      fragmentName: "questionFieldsItem",
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeQuestionFieldsInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `QuestionFields:${id}`,
      fragment: questionFieldsFragments.questionFields,
      fragmentName: "questionFieldsItem",
      data,
    });
  } catch (e) {
    return;
  }
};

/**
 * @deprecated use getQuestionFromCacheV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getQuestionFromCache = ({ id }) => {
  try {
    const queryData = client.readFragment({
      id: `Question:${id}`,
      fragment: questionFragments.question,
      fragmentName: "questionItem",
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

/**
 * @deprecated use writeQuestionInCacheV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const writeQuestionInCache = ({ id, data }) => {
  try {
    client.writeFragment({
      id: `Question:${id}`,
      fragment: questionFragments.question,
      fragmentName: "questionItem",
      data,
    });
  } catch (e) {
    return;
  }
};

export const getAssessmentToolsByQuestionSubmissionIdFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getAssessmentToolsByQuestionSubmissionIdQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getQuestionSubmissionFromCache = ({ id, userFilter = "" }) => {
  try {
    const queryData = client.readFragment({
      id: `QuestionSubmission:${id}`,
      fragment: questionSubmissionFragments.questionSubmission,
      fragmentName: "questionSubmissionItem",
      variables: { userFilter },
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeQuestionSubmissionInCache = ({
  id,
  data,
  userFilter = "",
}) => {
  try {
    client.writeFragment({
      id: `QuestionSubmission:${id}`,
      fragment: questionSubmissionFragments.questionSubmission,
      fragmentName: "questionSubmissionItem",
      data,
      variables: { userFilter },
    });
  } catch (e) {
    return;
  }
};

export const getQuestionTopicsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getQuestionTopicsQuery,
      variables,
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeQuestionTopicsInCache = (variables, data) => {
  try {
    client.writeQuery({
      query: getQuestionTopicsQuery,
      variables,
      data,
    });
    return;
  } catch (e) {
    return;
  }
};

export const getOrganizationGradesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationGradesQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getOrganizationSubjectsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationSubjectsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getSchoolQuestionLibraryFeedFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getSchoolQuestionLibraryFeedQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getToddleQuestionLibraryFeedFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getToddleQuestionLibraryFeedQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getQuestionLibraryQuestionFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getQuestionLibraryQuestionQuery,
      variables,
    });
    return _.get(queryData, "node") || {};
  } catch (e) {
    return {};
  }
};

export const getSyllabusContentTopicsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getSyllabusContentTopicsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

/**
 * @deprecated Use `getWorksheetQuestionWiseInsightsFromCacheV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const getWorksheetQuestionWiseInsightsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getWorksheetQuestionWiseInsightsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getWorksheetQuestionWiseInsightsFromCacheV2 = variables => {
  try {
    const queryData = client.readQuery({
      query: getWorksheetQuestionWiseInsightsV2Query,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

/**
 * @deprecated Use `getWorksheetScoreDistributionFromCacheV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const getWorksheetScoreDistributionFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getWorksheetScoreDistributionQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};
export const getWorksheetScoreDistributionFromCacheV2 = variables => {
  try {
    const queryData = client.readQuery({
      query: getWorksheetScoreDistributionQueryV2,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getQuestionSubmissionConnectionFromCache = ({
  id,
  userFilter = "",
}) => {
  try {
    const queryData = client.readFragment({
      id: `QuestionSubmissionConnection:StudentAssignmentSubmission:${id}.questionSubmissions`,
      fragment:
        questionSubmissionConnectionFragments.questionSubmissionConnection,
      fragmentName: "questionSubmissionConnectionItem",
      variables: { userFilter },
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeQuestionSubmissionConnectionInCache = ({
  id,
  data,
  userFilter = "",
}) => {
  try {
    client.writeFragment({
      id: `QuestionSubmissionConnection:StudentAssignmentSubmission:${id}.questionSubmissions`,
      fragment:
        questionSubmissionConnectionFragments.questionSubmissionConnection,
      fragmentName: "questionSubmissionConnectionItem",
      data,
      variables: { userFilter },
    });
  } catch (e) {
    return;
  }
};

/**
 * @deprecated use getStudentQuestionDetailsQueryFromCacheV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getStudentQuestionDetailsQueryFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getStudentQuestionDetailsQuery,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getStudentAssignmentDetailsForScorebookFromCache = ({
  id,
  showOnlySubmitted,
  userFilter,
  entityEventsFilter,
  userArchivedFilters,
}) => {
  try {
    const queryData = client.readQuery({
      query: getStudentAssignmentDetailsQueryV2,
      variables: {
        id,
        showOnlySubmitted,
        userFilter,
        entityEventsFilter,
        userArchivedFilters,
      },
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeStudentAssignmentDetailsForScorebookInCache = ({
  id,
  data,
  showOnlySubmitted,
  userFilter,
  entityEventsFilter,
  userArchivedFilters,
}) => {
  try {
    client.writeFragment({
      id: `StudentAssignment:${id}`,
      fragment: studentAssignmentFragment.studentAssignmentDetails,
      fragmentName: "studentAssignmentItem",
      variables: {
        showOnlySubmitted,
        userFilter,
        entityEventsFilter,
        userArchivedFilters,
      },
      data,
    });
  } catch (e) {}
};

export const getStudentAssignmentFromCacheV2 = ({
  id,
  entityEventsFilter,
  userFilter,
}) => {
  try {
    const queryData = client.readQuery({
      query: getStudentAssignmentQueryV2,
      variables: {
        id,
        entityEventsFilter,
        userFilter,
      },
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeStudentAssignmentInCacheV2 = ({
  id,
  data,
  entityEventsFilter,
  userFilter,
}) => {
  try {
    client.writeQuery({
      query: getStudentAssignmentQueryV2,
      variables: {
        id,
        entityEventsFilter,
        userFilter,
      },
      data: {
        node: data,
      },
    });
  } catch (e) {
    return {};
  }
};

/** Get question details from cache
 * @param {string} id - Question ID
 * @param {string} userType - User type (e.g., "student")
 * @returns {object} Question details
 */
export const getQuestionDetails = (id, userType) => {
  const isStudent = _.isEqual(userType, "student");
  const fragment = isStudent
    ? questionFragments.studentQuestionDetailsV2
    : questionFragments.questionV2;

  try {
    const queryData = client.readFragment({
      id: `Question:${id}`,
      fragment,
      fragmentName: "questionItem",
    });
    return queryData;
  } catch (e) {
    return {};
  }
};

export const getAssessmentQuestionsFromCacheV2 = variables => {
  try {
    const queryData = client.readQuery({
      query: getAssessmentQuestionsQueryV2,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getQuestionFromCacheV2 = ({ id, userType = "staff" }) => {
  try {
    const isStudent = _.isEqual(userType, "student");
    const queryData = client.readFragment({
      id: `Question:${id}`,
      fragment: isStudent
        ? questionFragments.studentQuestionDetailsV2
        : questionFragments.questionV2,
      fragmentName: "questionItem",
    });

    return queryData;
  } catch (e) {
    return {};
  }
};

export const writeQuestionInCacheV2 = ({ id, data, userType = "staff" }) => {
  try {
    const isStudent = _.isEqual(userType, "student");
    client.writeFragment({
      id: `Question:${id}`,
      fragment: isStudent
        ? questionFragments.studentQuestionDetailsV2
        : questionFragments.questionV2,
      fragmentName: "questionItem",
      data,
    });
  } catch (e) {
    return;
  }
};

export const getStudentQuestionDetailsQueryFromCacheV2 = variables => {
  try {
    const queryData = client.readQuery({
      query: getStudentQuestionDetailsQueryV2,
      variables,
    });

    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeAssessmentQuestionsInCacheV2 = (variables, data) => {
  try {
    client.writeQuery({
      query: getAssessmentQuestionsQueryV2,
      variables,
      data,
    });
  } catch (err) {
    return;
  }
};

export const getAIConversationsFromCache = variables => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getAIConversationsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  return _.get(queryData, "node", {});
};

export const writeAIConversationsInCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getAIConversationsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};
